// Per-user, per-lesson watch progress — users/{uid}/masterclassProgress/{videoId}.
// The finer layer beneath the class-level watchedMembers 완료 signal: resume
// position today, chapter completion once classes carry chapters[]. Doc shape:
//   { lastPositionSec, watchedSecTotal, lastChapterIdx, completedChapterIdxs,
//     viewed?, seededFrom?, seededAt?, updatedAt }
// Seeded docs (launch migration) are written server-side with the admin SDK.
// Client writes are best-effort (errors swallowed) so the lesson page never
// breaks while the rules for this subcollection are not yet deployed.
import { db } from "@/firebaseConfig";
import { collection, deleteField, doc, getDoc, getDocs, increment, setDoc } from "firebase/firestore";
import { noteFailure } from "@/utils/silentFailure";
import { markEngineDirty } from "@/services/engineBus";

function progressDocRef(uid, videoId) {
  if (!uid || !videoId) return null;
  return doc(db, "users", String(uid), "masterclassProgress", String(videoId));
}

// ISO week key ("2026-W30") — buckets the weekly watched-minutes counter that
// the home greeting/card read against the member's weekly goal.
export function isoWeekKey(d = new Date()) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

export default {
  // All of this member's progress docs, keyed by videoId.
  async fetchMine(uid) {
    if (!uid) return {};
    try {
      const snap = await getDocs(collection(db, "users", String(uid), "masterclassProgress"));
      const map = {};
      snap.forEach((d) => {
        map[d.id] = d.data();
      });
      return map;
    } catch (e) {
      noteFailure('masterclassProgressService.fetchMine', e);
      return {};
    }
  },

  async load(uid, videoId) {
    const ref = progressDocRef(uid, videoId);
    if (!ref) return null;
    try {
      const snap = await getDoc(ref);
      return snap.exists() ? snap.data() : null;
    } catch (e) {
      return null;
    }
  },

  // Merge-write the resume pointer (+ cumulative natural-play seconds). Real
  // play-time also lands in the _summary doc's per-week bucket (map-increment,
  // no read needed) so weekly-goal standing is one doc read.
  async savePosition(uid, videoId, positionSec, watchedDeltaSec = 0) {
    markEngineDirty(); // progress moved — 완료 and 이어보기 may both change
    const ref = progressDocRef(uid, videoId);
    if (!ref) return false;
    try {
      await setDoc(
        ref,
        {
          lastPositionSec: Math.round(positionSec),
          ...(watchedDeltaSec > 0 && { watchedSecTotal: increment(Math.round(watchedDeltaSec)) }),
          updatedAt: Date.now(),
        },
        { merge: true }
      );
      if (watchedDeltaSec > 0) {
        // Nested map (not a dotted path — setDoc+merge treats dots literally).
        await setDoc(
          progressDocRef(uid, "_summary"),
          { weekSec: { [isoWeekKey()]: increment(Math.round(watchedDeltaSec)) }, updatedAt: Date.now() },
          { merge: true }
        );
      }
      return true;
    } catch (e) {
      noteFailure('masterclassProgressService.savePosition', e);
      return false; // rules not deployed yet / offline — resume just won't persist
    }
  },

  // Manual watched stamp (도장) — the member affirms they watched this lesson
  // off-platform (downloaded, another device). Satisfies the 노출 rule via
  // seededFrom while watchedSecTotal stays honest at its measured value.
  // Clears any prior opt-out so the pair is symmetric.
  async markWatched(uid, videoId) {
    markEngineDirty();
    const ref = progressDocRef(uid, videoId);
    if (!ref) return false;
    try {
      await setDoc(
        ref,
        { seededFrom: "manual", manualMarkedAt: Date.now(), exposureOptOut: deleteField(), updatedAt: Date.now() },
        { merge: true }
      );
      return true;
    } catch (e) {
      return false;
    }
  },

  // Withdraw the 노출 claim for a lesson — ONE rule: the member can always
  // correct the record, whether the ✓ came from their own stamp, the launch
  // seeding, or measured playback. Records an explicit override instead of
  // deleting anything: `watchedSecTotal` is left intact so weekly minutes stay
  // truthful, and only the 노출 claim is withdrawn (homeEngine honours
  // exposureOptOut ahead of every other signal).
  async unmarkWatched(uid, videoId) {
    const ref = progressDocRef(uid, videoId);
    if (!ref) return false;
    try {
      await setDoc(
        ref,
        {
          exposureOptOut: true,
          manualUnmarkedAt: Date.now(),
          seededFrom: deleteField(),
          manualMarkedAt: deleteField(),
          updatedAt: Date.now(),
        },
        { merge: true }
      );
      return true;
    } catch (e) {
      noteFailure('masterclassProgressService.unmarkWatched', e);
      return false;
    }
  },

  /** Minutes of real playback in the current ISO week, from the _summary doc. */
  weekMinutes(progressMap) {
    const sec = progressMap?._summary?.weekSec?.[isoWeekKey()] || 0;
    return Math.floor(sec / 60);
  },
};
